import { useState, useCallback, useRef } from 'react';
import type { GeocodedAddress } from '../types/address';
import { route } from '../utils/route';

interface UseGeocodingOptions {
  debounceMs?: number;
}

interface UseGeocodingReturn {
  searchAddress: (query: string) => Promise<GeocodedAddress[]>;
  searchNeighborhood: (query: string) => Promise<GeocodedAddress[]>;
  reverseGeocode: (lat: number, lon: number) => Promise<GeocodedAddress | null>;
  isLoading: boolean;
  error: string | null;
}

export function useGeocoding({ debounceMs = 300 }: UseGeocodingOptions = {}): UseGeocodingReturn {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const searchAddress = useCallback(async (query: string): Promise<GeocodedAddress[]> => {
    if (!query || query.trim().length < 3) return [];

    return new Promise((resolve) => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);

      debounceTimer.current = setTimeout(async () => {
        setIsLoading(true);
        setError(null);
        try {
          const params = new URLSearchParams({ address: query });
          const response = await fetch(`${route('api.location.resolve')}?${params}`, {
            credentials: 'include',
            method: 'GET',
            headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' }
          });
          if (!response.ok) throw new Error('Error al buscar la direccion');
          const addresses = await response.json();
          resolve(addresses || []);
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Error desconocido');
          resolve([]);
        } finally {
          setIsLoading(false);
        }
      }, debounceMs);
    });
  }, [debounceMs]);

  const searchNeighborhood = useCallback(async (query: string): Promise<GeocodedAddress[]> => {
    if (!query || query.trim().length < 2) return [];

    return new Promise((resolve) => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);

      debounceTimer.current = setTimeout(async () => {
        setIsLoading(true);
        setError(null);
        try {
          const params = new URLSearchParams({ neighborhood: query, is_country: 'on' });
          const response = await fetch(`${route('api.location.resolve')}?${params}`, {
            credentials: 'include',
            method: 'GET',
            headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' }
          });
          if (!response.ok) throw new Error('Error al buscar el barrio');
          const neighborhoods = await response.json();
          resolve(neighborhoods || []);
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Error desconocido');
          resolve([]);
        } finally {
          setIsLoading(false);
        }
      }, debounceMs);
    });
  }, [debounceMs]);

  const reverseGeocode = useCallback(async (lat: number, lon: number): Promise<GeocodedAddress | null> => {
    setIsLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ lat: lat.toString(), lon: lon.toString(), reverse: 'on' });
      const response = await fetch(`${route('api.location.resolve')}?${params}`, {
        credentials: 'include',
        method: 'GET',
        headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' }
      });
      if (!response.ok) throw new Error('Error al obtener la direccion');
      const result = await response.json();
      if (!result || !result.lat || !result.lon) return null;
      return result;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error desconocido');
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return { searchAddress, searchNeighborhood, reverseGeocode, isLoading, error };
}
